//
// Copyright (c) 2008-2025 Barry E. Mapen All Rights Reserved
//
import { useContext } from 'react';
import classNames from 'classnames';
import ReactPropTypes from 'prop-types';

import { FormContext } from './Form';

import * as patch from './PatchFunctions';

export function Input({
    name,
    id,
    type = "text",
    value,
    className = "",
    useUTC = false,
    disabled = false,
    clearOnDisable = false,
    readOnly = false,
    onChange,
    ...rest
}) {

    const { 
        initial, current, formId, formDisabled, formReadOnly, defaultOnChange,
    } = useContext(FormContext);

    // Extract the initial value for this named item in the form fields
    const initValue = patch.getPath(initial, name);

    // Use any parameters pass into this field
    const currId = id ? id : `${name}-${formId}`;
    const inputValue = value ? value : patch.getPath(current, name);
    const currDisabled = disabled || formDisabled;
    const currReadOnly = readOnly || formReadOnly;
    const currValue = (currDisabled && clearOnDisable) ? "" : inputValue; 

    const inputClass = classNames(
        'form-control',
        className,
        { 'is-dirty': currValue !== initValue }
    );

    //
    // If there is no value (null or undefined) we need to change it to an
    //  empty string before passing it into the input control
    //
    if (type === "date" || type === "datetime-local") {
        return(<InputDate 
            className={inputClass}
            type={type}
            name={name}
            id={currId}
            value={currValue ? currValue : ""}
            useUTC={useUTC}
            onChange={(e) => defaultOnChange(e, onChange)}
            disabled={currDisabled}
            readOnly={currReadOnly}
            {...rest}
        />)
    }
    else {
        return(<input 
            className={inputClass}
            type={type}
            name={name}
            id={currId}
            value={currValue ? currValue : ""}
            onChange={(e) => defaultOnChange(e, onChange)}
            disabled={currDisabled}
            readOnly={currReadOnly}
            {...rest}
        />)
    }
}

Input.propTypes = {
    name: ReactPropTypes.string.isRequired,
    type: ReactPropTypes.string,
    value: ReactPropTypes.any,
    onChange: ReactPropTypes.func,
}




function InputDate({
    type,
    value,
    onChange: formOnChange,
    useUTC,
    ...rest
}) {

    //
    // Assume we want the standard behavior (non-UTC) where the Form onChange
    //  handler, but we can override this below if we need the UTC handler to
    //  tweak the dates forward/backward in time so we display everything to the
    //  user in their local time zone, but maintain everything as UTC
    //
    let onChange = formOnChange;

    //
    // Determine the offset in milliseconds between local and UTC time
    //
    const offset = new Date().getTimezoneOffset() * 60 * 1000;

    const onDateTimeChangeUTC = (e) => {
        const { target: { name, value, type, checked } } = e;
        const utcDate = new Date(new Date(value+'Z').valueOf() - offset).toISOString();
        formOnChange({ target: { name, type, checked, value: utcDate }});
    }

    const onDateChangeUTC = (e) => {
        const { target: { name, value, type, checked } } = e;
        const utcDate = new Date(new Date(value+"T00:00:00.000Z").valueOf() - offset).toISOString();
        formOnChange({ target: { name, type, checked, value: utcDate }});
    }

    //
    // Must have a valid date to try to mainipulate it
    //
    if (value !== '') {
        if (useUTC) {
            //
            // Switch to using the UTC change handler and modify what is displayed
            //  into the local time zone
            //
            if (type === 'datetime-local') {
                onChange = onDateTimeChangeUTC;
                value = new Date(new Date(value).valueOf() + offset).toISOString();
            }
            else if (type === 'date') {
                onChange = onDateChangeUTC;
                value = new Date(new Date(value).valueOf() + offset).toISOString();
            }
        }
    }

    //
    // If this is a date only field, remove any time element from it
    //
    if (type === 'date') {
        // We must only pass in the date portion of this string which
        //  is everything before the T
        value = value.split('T')[0];
    }
    else if (type === 'datetime-local') {
        // We must remove the Z ending from the date string for it to be
        //  a valid input to an html input element
        value = value.split('Z')[0];
    }

    return(
        <input 
            type={type}
            value={value}
            onChange={onChange}
            {...rest}
        />
    );
}

